import { useEffect, useMemo, useCallback } from 'react'
import { useNotificationStore } from '@/stores/notificationStore'
import { useUser } from '@/stores/authStore'

interface HookOptions {
  status?: 'unread' | 'read' | 'archived'
  type?: string
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  limit?: number
  tripId?: string
}

export function useBackendNotificationsRealtime(options?: HookOptions) {
  const user = useUser()
  const notifications = useNotificationStore(state => state.notifications)
  const unreadCount = useNotificationStore(state => state.unreadCount)
  const storeLoading = useNotificationStore(state => state.loading)
  const error = useNotificationStore(state => state.error)
  const hasMore = useNotificationStore(state => state.hasMore)
  const isInitialized = useNotificationStore(state => state.isInitialized)
  const fetchNotifications = useNotificationStore(state => state.fetchNotifications)
  const refreshStore = useNotificationStore(state => state.refresh)
  const markAsRead = useNotificationStore(state => state.markAsRead)
  const markAllAsRead = useNotificationStore(state => state.markAllAsRead)
  const deleteNotification = useNotificationStore(state => state.deleteNotification)
  const deleteAllNotifications = useNotificationStore(state => state.deleteAllNotifications)

  useEffect(() => {
    if (!user) {
      return
    }

    const currentTripId = useNotificationStore.getState().fetchParams.tripId
    const newTripId = options?.tripId

    // Fetch notifications when:
    // 1. Not initialized yet, OR
    // 2. tripId has changed (including going from tripId to undefined or vice versa)
    const shouldFetch = !isInitialized || (currentTripId !== newTripId)

    if (shouldFetch && !storeLoading) {
      fetchNotifications({
        tripId: newTripId
      }).catch(() => {
        // Error is captured in the store; nothing else to do here.
      })
    }
  }, [user, isInitialized, storeLoading, fetchNotifications, options?.tripId])

  const filteredNotifications = useMemo(() => {
    let list = notifications

    if (options?.status) {
      list = list.filter(notification => notification.status === options.status)
    }

    if (options?.type) {
      list = list.filter(notification => notification.type === options.type)
    }

    if (options?.priority) {
      list = list.filter(notification => notification.priority === options.priority)
    }

    if (options?.limit) {
      list = list.slice(0, options.limit)
    }

    return list
  }, [notifications, options?.status, options?.type, options?.priority, options?.limit])

  const refresh = useCallback(() => {
    refreshStore().catch(() => {
      // Error handled in the store state.
    })
  }, [refreshStore])

  return {
    notifications: filteredNotifications,
    unreadCount,
    loading: !isInitialized || storeLoading,
    error,
    hasMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    refresh
  }
}

export function useNotificationBadgeCountRealtime() {
  const user = useUser()
  const unreadCount = useNotificationStore(state => state.unreadCount)
  const storeLoading = useNotificationStore(state => state.loading)
  const isInitialized = useNotificationStore(state => state.isInitialized)
  const fetchNotifications = useNotificationStore(state => state.fetchNotifications)
  const refreshStore = useNotificationStore(state => state.refresh)

  useEffect(() => {
    if (!user) {
      return
    }

    if (!isInitialized && !storeLoading) {
      fetchNotifications().catch(() => {
        // Errors handled in store state.
      })
    }
  }, [user, isInitialized, storeLoading, fetchNotifications])

  const refresh = useCallback(() => {
    refreshStore().catch(() => {
      // Store already captures the error.
    })
  }, [refreshStore])

  return {
    count: unreadCount,
    loading: !isInitialized || storeLoading,
    refresh
  }
}
